import React from 'react';
import { Box, Typography } from '@mui/material';
import IPhoneFrame, {
  StatusBar, NavBar, TabBar, HomeBar,
  IOS_FONT, IOS_BLUE, IOS_GRAY_BG, IOS_GRAY_FIELD, IOS_GRAY_TEXT, IOS_SEPARATOR,
} from './IPhoneFrame';
import {
  APP_LOGOS, APP_COLORS, CLASS_MEMBERSHIPS as SHARED_MEMBERSHIPS,
  ACHIEVEMENTS as SHARED_ACHIEVEMENTS, STUDENT, CLIENT, IMPACT,
} from '../sharedMockData';

// iOS display uses a concise format: "<teacher> · <class>"
const CLASS_MEMBERSHIPS = SHARED_MEMBERSHIPS.map(c => ({
  name: `${c.teacher} · ${c.name.split(' · ')[0]}`,
  code: c.code,
  app: c.app,
  appKey: c.appKey,
  color: APP_COLORS[c.appKey] || '#546e7a',
  logo: APP_LOGOS[c.appKey],
}));

const ACHIEVEMENTS_PROFILE = SHARED_ACHIEVEMENTS.slice(0, 3);

const RowLink = ({ icon, label, sub, last }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.5,
    py: 1.2, px: 1.8,
    borderBottom: last ? 'none' : `0.5px solid ${IOS_SEPARATOR}`,
    cursor: 'pointer',
  }}>
    <Box sx={{ fontSize: 20, width: 28, textAlign: 'center' }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontFamily: IOS_FONT, fontSize: '15px', color: '#000' }}>{label}</Typography>
      {sub && (
        <Typography sx={{ fontFamily: IOS_FONT, fontSize: '11px', color: IOS_GRAY_TEXT }}>{sub}</Typography>
      )}
    </Box>
    <Box sx={{ color: IOS_GRAY_TEXT, fontSize: '18px', fontWeight: 300 }}>›</Box>
  </Box>
);

const IosProfileMockup = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: '#eceff1', minHeight: '100%' }}>
    <IPhoneFrame
      title="6. Profile"
      caption="Avatar, class memberships, achievements, settings entry. Sign-out clears Keychain session token and webview cookies."
    >
      <StatusBar />
      <NavBar title="Profile" largeTitle trailingButton={{ label: 'Edit' }} />
      <Box sx={{ position: 'absolute', top: 44 + 100, left: 0, right: 0, bottom: 83, overflow: 'auto' }}>
        {/* Avatar + identity */}
        <Box sx={{ textAlign: 'center', pt: 1, pb: 2 }}>
          <Box sx={{
            width: 96, height: 96, borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: IOS_FONT, fontSize: '38px', fontWeight: 700, color: '#fff',
            mx: 'auto', mb: 1.5,
            boxShadow: '0 6px 18px rgba(102, 126, 234, 0.35)',
          }}>{STUDENT.initials}</Box>
          <Typography sx={{ fontFamily: IOS_FONT, fontSize: '20px', fontWeight: 700 }}>
            {STUDENT.full}
          </Typography>
          <Typography sx={{ fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT }}>
            {CLIENT.longName} · {STUDENT.grade}
          </Typography>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            bgcolor: '#E8F5E9', color: '#2E7D32',
            fontFamily: IOS_FONT, fontSize: '11px', fontWeight: 600,
            px: 1, py: 0.3, borderRadius: 10, mt: 1,
          }}>✓ Enrolled via MDM</Box>
        </Box>

        <Box sx={{ px: 2 }}>
          {/* Stats pill row */}
          <Box sx={{
            display: 'flex',
            bgcolor: '#fff',
            borderRadius: '14px',
            border: `0.5px solid ${IOS_SEPARATOR}`,
            p: 1.5, mb: 2,
          }}>
            {[
              { v: IMPACT.total.toLocaleString(), l: 'Impact Pts' },
              { v: String(IMPACT.classesJoined), l: 'Classes' },
              { v: String(IMPACT.badges),         l: 'Badges' },
            ].map((s, i, arr) => (
              <Box key={i} sx={{
                flex: 1, textAlign: 'center',
                borderRight: i < arr.length - 1 ? `0.5px solid ${IOS_SEPARATOR}` : 'none',
              }}>
                <Typography sx={{ fontFamily: IOS_FONT, fontSize: '20px', fontWeight: 700 }}>{s.v}</Typography>
                <Typography sx={{ fontFamily: IOS_FONT, fontSize: '11px', color: IOS_GRAY_TEXT }}>{s.l}</Typography>
              </Box>
            ))}
          </Box>

          {/* Class memberships */}
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT,
            textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600,
            mb: 1, ml: 1,
          }}>Classes</Typography>
          <Box sx={{ bgcolor: '#fff', borderRadius: '14px', border: `0.5px solid ${IOS_SEPARATOR}`, mb: 2, overflow: 'hidden' }}>
            {CLASS_MEMBERSHIPS.map((c, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                py: 1.2, px: 1.5,
                borderBottom: i < CLASS_MEMBERSHIPS.length - 1 ? `0.5px solid ${IOS_SEPARATOR}` : 'none',
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5,
                  bgcolor: '#fff',
                  border: `0.5px solid ${IOS_SEPARATOR}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                }}>
                  {c.logo ? (
                    <Box component="img" src={c.logo} alt={c.app} sx={{ width: 30, height: 30, objectFit: 'contain' }} />
                  ) : (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: c.color }} />
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: IOS_FONT, fontSize: '14px', fontWeight: 600 }}>{c.name}</Typography>
                  <Typography sx={{ fontFamily: IOS_FONT, fontSize: '11px', color: IOS_GRAY_TEXT }}>
                    Code: {c.code} · {c.app}
                  </Typography>
                </Box>
                <Box sx={{ color: IOS_GRAY_TEXT, fontSize: '18px', fontWeight: 300 }}>›</Box>
              </Box>
            ))}
          </Box>

          {/* Achievements */}
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT,
            textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600,
            mb: 1, ml: 1,
          }}>Recent Achievements</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {ACHIEVEMENTS_PROFILE.map((a, i) => (
              <Box key={i} sx={{
                flex: 1,
                bgcolor: '#fff',
                borderRadius: '14px',
                border: `0.5px solid ${IOS_SEPARATOR}`,
                p: 1.2, textAlign: 'center',
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '50%',
                  bgcolor: `${a.color}22`,
                  mx: 'auto',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '18px',
                  mb: 0.5,
                }}>{a.emoji}</Box>
                <Typography sx={{ fontFamily: IOS_FONT, fontSize: '10px', fontWeight: 500, lineHeight: 1.1 }}>{a.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Settings list */}
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT,
            textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600,
            mb: 1, ml: 1,
          }}>Settings</Typography>
          <Box sx={{ bgcolor: '#fff', borderRadius: '14px', border: `0.5px solid ${IOS_SEPARATOR}`, mb: 2, overflow: 'hidden' }}>
            <RowLink icon="🔔" label="Notifications" sub="APNS enabled" />
            <RowLink icon="🛡️" label="Content Filter" sub="MDM-managed · baseline" />
            <RowLink icon="👤" label="Face ID" sub="Required after 5 min idle" />
            <RowLink icon="🌐" label="Language" sub="English (US)" />
            <RowLink icon="ℹ️" label="About AchieveDXP iOS" sub="v1.0 (build 1)" last />
          </Box>

          {/* Sign out */}
          <Box sx={{
            bgcolor: '#fff', borderRadius: '14px',
            border: `0.5px solid ${IOS_SEPARATOR}`,
            textAlign: 'center',
            py: 1.5,
            fontFamily: IOS_FONT, fontSize: '15px', fontWeight: 500,
            color: '#FF3B30',
            mb: 2,
          }}>Sign Out</Box>
        </Box>
      </Box>
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <TabBar activeIndex={4} />
      </Box>
      <HomeBar />
    </IPhoneFrame>
  </Box>
);

export default IosProfileMockup;
